import {
  useInfiniteQuery,
  type InfiniteData,
  type UseInfiniteQueryOptions,
  type UseInfiniteQueryResult,
} from '@tanstack/react-query';
import { useMemo } from 'react';
import {
  PaginateType,
  type TPageParams,
  type TPaginationResponse,
} from '@shared/types';
import { AxiosError } from 'axios';
import { type TPaginateQueryKey } from '@shared/config';

interface IData<T> {
  data: TPaginationResponse<T[]> | undefined;
}

/**
 *
 * Хук для запросов с бесконечной пагинацией.
 * Хук должен возвращаться любым хуком для создания запроса с бесконечной пагинацией.
 *
 * @template T - Тип возвращаемого массива данных. Передается как дженерик.
 *
 * @param options - Настройки запроса
 *
 * @returns Объект со свойством "data" - данные типа {@link TPaginationResponse<T>} и остальными свойствами (isFetching, isError, ...) {@link Omit<UseInfiniteQueryResult<TData>, 'data'> & IData<T>}
 */

export function useInfiniteRequest<
  T,
  TQueryFnData extends TPaginationResponse<T[]> = TPaginationResponse<T[]>,
  TError extends AxiosError = AxiosError,
  TData extends InfiniteData<TQueryFnData, TPageParams> = InfiniteData<
    TQueryFnData,
    TPageParams
  >,
  TQueryKey extends TPaginateQueryKey = TPaginateQueryKey,
>(
  options: Omit<
    UseInfiniteQueryOptions<
      TQueryFnData,
      TError,
      TData,
      TQueryKey,
      TPageParams
    >,
    'getNextPageParam'
  >
): Omit<UseInfiniteQueryResult<TData>, 'data'> & IData<T> {
  const fullOptions: UseInfiniteQueryOptions<
    TQueryFnData,
    TError,
    TData,
    TQueryKey,
    TPageParams
  > = {
    ...options,
    getNextPageParam: lastPage => {
      if (options.queryKey[1].paginate === PaginateType.NONE) {
        return undefined;
      }

      const { currentPage, totalPages } = lastPage.pagination;

      if (currentPage < totalPages) {
        return {
          page: currentPage + 1,
          perPage: lastPage.pagination.perPage,
        };
      }

      return undefined;
    },
  };

  const { data: infiniteData, ...rest } = useInfiniteQuery<
    TQueryFnData,
    TError,
    TData,
    TQueryKey,
    TPageParams
  >(fullOptions);

  const data = useMemo(() => {
    if (!infiniteData) {
      return undefined;
    }

    if (options.queryKey[1].paginate === PaginateType.NONE) {
      return infiniteData.pages[0];
    }

    const allData = infiniteData.pages.flatMap(page => page.data);
    const lastPage = infiniteData.pages[infiniteData.pages.length - 1];

    return {
      data: allData,
      pagination: lastPage.pagination,
    };
  }, [infiniteData, options.queryKey]);

  return { data, ...rest };
}
